import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import marketContextService from './MarketContextService.js';
import writingStyleService from "./CommunicationStyleService.js";
import aiUsageStatsService from './AIUsageStatsService.js';
import { createNetworkError } from '../utils/StandardErrorFormat.js';
import logger from '../utils/logger.js';
import { getModelById } from '../config/aiModels.js';
// #229 decomposition — Phase 1B/1C helpers extracted into focused modules
// under newsletter-ai/. Only `buildPromptContext` is imported here for
// class-internal use (by _applySeriesContextIfFlagged); the rest are
// re-exported at the bottom of this file via `export ... from ...`.
import { buildPromptContext } from './newsletter-ai/series-prompt-context.js';
// #263 — regeneration cluster extracted (Wave 1 #271, Wave 2 #272).
// #229 Wave 3 — subject-line cluster extracted (#273).
// #229 Wave 4 — generateNewsletter core + placeholder-generation cluster
// extracted (this PR). Class methods below are 1-line delegates.
import {
  buildStyleInstructions as _buildStyleInstructionsImpl,
  rewriteSelection as _rewriteSelectionImpl,
  regenerateWithStyle as _regenerateWithStyleImpl,
  regenerateSection as _regenerateSectionImpl,
} from './newsletter-ai/regeneration.js';
import {
  generateSubjectLines as _generateSubjectLinesImpl,
  parseSubjectLineResponse as _parseSubjectLineResponseImpl,
  isSubjectLineCompliant as _isSubjectLineCompliantImpl,
} from './newsletter-ai/subject-lines.js';
import {
  generateNewsletter as _generateNewsletterImpl,
} from './newsletter-ai/generate-newsletter.js';
import {
  generatePlaceholder as _generatePlaceholderImpl,
  analyzeStyle as _analyzeStyleImpl,
  generateAllPlaceholderValues as _generateAllPlaceholderValuesImpl,
  generatePlaceholderValue as _generatePlaceholderValueImpl,
  buildPlaceholderPrompt as _buildPlaceholderPromptImpl,
  validateAndFormatPlaceholder as _validateAndFormatPlaceholderImpl,
  getPlaceholderFallbackValue as _getPlaceholderFallbackValueImpl,
} from './newsletter-ai/placeholder-generation.js';

const client = generateClient();

/**
 * Service for AI Newsletter Generation using the direct AWS API Gateway
 * Enhanced with market context injection and personalization (US-047)
 * Part of the AI Integration Plan Phase 1 & Phase 2-MVP
 * Circular dependencies fixed with dependency injection pattern
 */
class NewsletterAIService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('NewsletterAIService');

    // Use proxy in development, amplify_outputs.json in production (auto-synced Cognito — #310)
    this.apiEndpoint = import.meta.env.DEV
      ? '/api/ai/generate'
      : (this._resolveProductionEndpoint());
    this.initialized = true;
    this.marketContextService = marketContextService;
    this.writingStyleService = writingStyleService;

    // Set this service on the writing style service to avoid circular dependency
    this.writingStyleService.setAIService(this);

    // Per-section regeneration cache (Sprint 4 #66).
    // Keyed by `${sectionKey}|${tone}|${YYYY-MM-DD}` so an advisor isn't billed
    // for the same regen twice in a session, but a new day or tone refreshes it.
    this._sectionRegenCache = new Map();
  }

  // Resolve the production AI API endpoint from amplify_outputs.json (auto-synced
  // Cognito authorizer — #310) with fallback to env var and hardcoded legacy URL.
  _resolveProductionEndpoint() {
    try {
      // amplify_outputs.json is statically imported in main.jsx and bundled by Vite,
      // so we can read it from the globally configured Amplify instance. But for
      // simplicity and to avoid circular deps, we read the JSON directly.
      // Vite resolves JSON imports at build time.
      const amplifyConfig = typeof window !== 'undefined' && window.__AMPLIFY_OUTPUTS__;
      if (amplifyConfig?.custom?.newsletter_ai_api_url) {
        return amplifyConfig.custom.newsletter_ai_api_url;
      }
    } catch { /* not available */ }
    return import.meta.env.VITE_NEWSLETTER_AI_API_URL
      || 'https://blhrv7e0c1.execute-api.us-west-2.amazonaws.com/prod/generate';
  }

  /**
   * Initialize the service (kept for compatibility)
   */
  async initialize() {
    // Already initialized with direct endpoint
    return;
  }

  /**
   * Sprint 6 M3 (#151 P0 #1) — pre-flight daily-spend gate. Call from
   * the top of every public AI method before the network round-trip.
   * Throws `QuotaExceededError` if today's accumulated cost is over the
   * per-user daily budget. The error bubbles to the UI which catches by
   * `err.name === 'QuotaExceededError'` and renders a friendly message.
   *
   * Quietly returns (no throw) when the user is unauthenticated or
   * AIUsageStatsService isn't available — those failure modes are
   * already handled elsewhere and we don't want the cap to mask them.
   */
  async _assertWithinDailyBudget() {
    try {
      const user = await getCurrentUser();
      const userId = user?.userId;
      if (!userId) return;
      await aiUsageStatsService.assertWithinDailyBudget(userId);
    } catch (err) {
      // Re-throw QuotaExceededError so callers can surface it; swallow
      // anything else so this guard never breaks an already-broken auth
      // or service path.
      if (err?.name === 'QuotaExceededError') throw err;
    }
  }

  /**
   * Get model configuration for routing.
   * Falls back to DEFAULT_MODEL when modelId is invalid or not provided.
   * @param {string} modelId - Key from AI_MODELS (e.g. 'claude-sonnet-5', 'gpt-4o')
   * @returns {Object} Model config with provider, id, name, etc.
   */
  getModelConfig(modelId) {
    return getModelById(modelId);
  }

  /**
   * Update usage statistics via GraphQL backend
   */
  async updateUsageStats(metadata, provider = null) {
    try {
      const tokensUsed = metadata?.tokensUsed?.total || 0;
      const estimatedCost = metadata?.estimatedCost?.total || 0;

      // Update stats in backend via AIUsageStatsService (with provider for per-provider tracking)
      await aiUsageStatsService.updateStats({
        tokensUsed,
        cost: estimatedCost,
        feature: 'newsletter',
        provider: provider || metadata?.provider || null
      });

      this.logger.info('AI usage stats updated:', { tokensUsed, estimatedCost, feature: 'newsletter', provider });
    } catch (error) {
      this.logger.error('Error updating usage stats:', error);
      // Don't throw - usage tracking failure shouldn't break newsletter generation
    }
  }

  /**
   * Get authentication token for API requests.
   *
   * Issue #304 — accepts `{ forceRefresh: true }` so callers that hit a
   * 401 can retry with a freshly-minted Cognito ID token (1hr TTL).
   * Default is the cached session — same behavior as before.
   *
   * @param {{ forceRefresh?: boolean }} [opts]
   */
  async getAuthToken({ forceRefresh = false } = {}) {
    try {
      const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        createNetworkError('No authentication token available');
      }

      return token;
    } catch (error) {
      this.logger.error('Failed to get auth token:', error);

      if (error.name === 'ServiceError') {
        throw error;
      }

      createNetworkError('Failed to get authentication token', error);
    }
  }

  /**
   * Get advisor writing preferences from UserProfile
   * @private
   * @param {string} userId - Optional user ID, if not provided will get current user
   * @returns {Promise<Object>} Writing preferences object
   */
  async _getAdvisorPreferences(userId = null) {
    try {
      let targetUserId = userId;

      if (!targetUserId) {
        const user = await getCurrentUser();
        targetUserId = user.userId;
      }

      const { data: profiles } = await client.models.UserProfile.list({
        filter: { userId: { eq: targetUserId } }
      });

      if (profiles && profiles.length > 0 && profiles[0].writingPreferences) {
        return profiles[0].writingPreferences;
      }

      return this._getDefaultPreferences();
    } catch (error) {
      this.logger.warn('Failed to fetch writing preferences, using defaults:', error);
      return this._getDefaultPreferences();
    }
  }

  /**
   * Get default writing preferences
   * @private
   */
  _getDefaultPreferences() {
    return {
      style: 'professional',
      tone: 'professional',
      complexityLevel: 'intermediate',
      writingSampleText: '',
      writingSampleAnalyzed: false,
      styleSignature: null,
      preferredTopics: [],
      avoidTopics: [],
      customGuidelines: '',
      examplePhrases: []
    };
  }

  /**
   * Sprint 4 #67 — Default 9-dimension CommunicationStyle used when the
   * advisor has no saved style profile. Sensible "professional, balanced,
   * advisory" defaults that produce clear, audience-appropriate prose.
   * @private
   */
  _getDefaultCommunicationStyle() {
    return {
      tone: 'professional',
      formality: 'balanced',
      confidence: 'balanced',
      personalTouch: 'medium',
      technicalLevel: 'intermediate',
      sentenceComplexity: 'moderate',
      commonPhrases: [],
      signatureGreeting: 'Dear {{client_name}},',
      signatureClosing: 'Best regards,',
      analyzedFrom: 'system_default'
    };
  }

  /**
   * Sprint 4 #67 — Fetch advisor's saved CommunicationStyle (the 9-dimension
   * voice profile) from CommunicationStyleService. Never throws — returns
   * null on failure so callers can fall back to defaults.
   * @private
   * @returns {Promise<Object|null>} 9-dimension style or null
   */
  async _getAdvisorCommunicationStyle() {
    try {
      if (!this.writingStyleService || typeof this.writingStyleService.getCommunicationStyle !== 'function') {
        return null;
      }
      const style = await this.writingStyleService.getCommunicationStyle();
      return style || null;
    } catch (error) {
      this.logger.warn('Failed to fetch CommunicationStyle, will use defaults:', error?.message || error);
      return null;
    }
  }

  /**
   * Sprint 4 #67 — Build an LLM instruction block from a 9-dimension
   * CommunicationStyle profile. The block is designed to be prepended to
   * the system prompt so the LLM matches the advisor's saved voice on
   * EVERY generation (full newsletter, section regeneration, subject lines).
   *
   * Used by:
   *   - generateNewsletter        (full newsletter generation)
   *   - regenerateWithStyle       (section/content rewrite — already takes
   *                                a style profile, but the same block
   *                                shape is used for consistency)
   *
   * @param {Object|null} style - 9-dim style or null/undefined for defaults
   * @returns {string} Style instruction block (always non-empty)
   */
  _buildCommunicationStyleBlock(style) {
    const profile = style || this._getDefaultCommunicationStyle();
    const lines = [];

    lines.push('=== ADVISOR VOICE PROFILE ===');
    lines.push('You are writing on behalf of a financial advisor. Match their established voice EXACTLY across all 9 dimensions below. Do not invent your own style.');
    lines.push('');

    // 1. Tone
    if (profile.tone) {
      lines.push(`- Tone: ${profile.tone} (this is how the advisor sounds emotionally to the reader)`);
    }
    // 2. Formality
    if (profile.formality) {
      lines.push(`- Formality: ${profile.formality} (e.g. casual = contractions OK, formal = no contractions)`);
    }
    // 3. Confidence
    if (profile.confidence) {
      lines.push(`- Confidence: ${profile.confidence} (confident = "we expect", cautious = "may", balanced = mix)`);
    }
    // 4. Personal touch
    if (profile.personalTouch) {
      lines.push(`- Personal touch: ${profile.personalTouch} (high = first-person, second-person address; low = neutral third person)`);
    }
    // 5. Technical level
    if (profile.technicalLevel) {
      lines.push(`- Technical level: ${profile.technicalLevel} (beginner = explain jargon; advanced = assume knowledge)`);
    }
    // 6. Sentence complexity / structure
    if (profile.sentenceComplexity) {
      lines.push(`- Sentence complexity: ${profile.sentenceComplexity} (short = punchy 10-15 words; long = 25-35 words)`);
    }
    // 7. Vocabulary level (often co-stored with technicalLevel; surface if separately set)
    if (profile.vocabularyLevel && profile.vocabularyLevel !== profile.technicalLevel) {
      lines.push(`- Vocabulary: ${profile.vocabularyLevel}`);
    }

    // 8. Signature greeting (only surface as guidance, not as literal text the LLM must paste)
    if (profile.signatureGreeting) {
      lines.push(`- Greeting style: open with "${profile.signatureGreeting}" (or a close variant)`);
    }
    // 9. Signature closing
    if (profile.signatureClosing) {
      lines.push(`- Closing style: sign off with "${profile.signatureClosing}" (or a close variant)`);
    }

    // Common phrases (advisor's lexical fingerprint) — only when we have them
    const phrases = Array.isArray(profile.commonPhrases) ? profile.commonPhrases.filter(Boolean) : [];
    if (phrases.length > 0) {
      lines.push('');
      lines.push(`- Common phrases the advisor uses (work 1-2 in naturally where they fit): ${phrases.slice(0, 5).join('; ')}`);
    }

    lines.push('');
    lines.push('=== END ADVISOR VOICE PROFILE ===');
    lines.push('');

    return lines.join('\n');
  }

  /**
   * Generate newsletter content using AI with full personalization (US-047)
   * @param {Object} params - Generation parameters
   * @param {string} params.templateContent - Template content/structure
   * @param {Object} params.clientData - Client information
   * @param {string} params.style - Writing style (optional, will use advisor preferences if not provided)
   * @param {Array} params.sections - Sections to include
   * @param {boolean} params.includeMarketContext - Whether to include market context
   * @param {boolean} params.generatePlaceholders - Whether to auto-generate placeholder values
   * @param {Object} params.template - Full template object with placeholders array
   * @param {string} params.newsletterType - Type of newsletter ('weekly', 'monthly', 'quarterly')
   * @param {string} params.userId - Optional user ID for fetching preferences
   * @param {string} params.seriesId - Phase 1B (#168, #223): when provided
   *   AND `VITE_USE_NICHE_FRAMING=true`, the prompt is enriched with niche
   *   framing, audience cues, cadence, and advisor-identity placeholders
   *   resolved from PublicationSeries + UserProfile.
   */

  /**
   * Phase 1B (#223) — bridge between generateNewsletter and the named
   * helpers `buildPromptContext` / `applyBrandingToHtml` that shipped in
   * #168 but were never wired into a runtime call path.
   *
   * Returns enhanced { systemPrompt, additionalData } when the flag is on
   * AND a seriesId is supplied. Identity passthrough otherwise. Errors are
   * swallowed so a flaky GraphQL call never breaks generation — niche
   * framing is best-effort.
   */
  async _applySeriesContextIfFlagged({ systemPrompt, additionalData, seriesId } = {}) {
    const baseAdditional = additionalData || {};
    const basePrompt = systemPrompt || '';
    if (!seriesId) return { systemPrompt: basePrompt, additionalData: baseAdditional };
    if (import.meta.env?.VITE_USE_NICHE_FRAMING !== 'true') {
      return { systemPrompt: basePrompt, additionalData: baseAdditional };
    }
    try {
      const ctx = await buildPromptContext({ seriesId });
      const cuesBlock = ctx.audienceCues?.length > 0
        ? `${ctx.audienceCues.join('\n')}\n`
        : '';
      const cadenceBlock = ctx.cadence ? `Frequency: ${ctx.cadence} cadence.\n` : '';
      const prefix = cuesBlock + cadenceBlock;
      // Resolved placeholders win over caller-supplied values per spec § 2.2
      // (#131 — UserProfile is canonical for advisor identity).
      const merged = { ...baseAdditional, ...(ctx.placeholders || {}) };
      // #105 — observability: emit a structured event so CloudWatch Logs
      // Insights queries can derive engagement rate. See
      // docs/03-technical/PHASE-1B-OBSERVABILITY.md for the schema + queries.
      this.logger?.info?.('phase1b.series_context.engaged', {
        seriesId,
        niche: ctx.niche,
        cadence: ctx.cadence,
        audienceCuesCount: ctx.audienceCues?.length || 0,
      });
      return {
        systemPrompt: prefix ? `${prefix}\n${basePrompt}` : basePrompt,
        additionalData: merged,
      };
    } catch (err) {
      // #105 — observability: structured failure event so the dashboard
      // can track helper failure rate over time.
      this.logger?.warn?.('phase1b.series_context.failed', {
        seriesId,
        reason: err?.message || String(err),
      });
      return { systemPrompt: basePrompt, additionalData: baseAdditional };
    }
  }

  async generateNewsletter(params) {
    return _generateNewsletterImpl(this, params);
  }

  /**
   * Helper method to inject market context into template content
   * @private
   * @param {string} content - Template content with placeholders
   * @param {Object} injections - Market context injections
   * @returns {string} Content with injected market context
   */
  _injectMarketContext(content, injections) {
    let enhancedContent = content;

    // Replace standard placeholders with market context
    const placeholders = {
      '{MARKET_CONTEXT}': injections.MARKET_CONTEXT_COMBINED || '',
      '{MARKET_CONTEXT_WEEKLY}': injections.MARKET_CONTEXT_WEEKLY || '',
      '{MARKET_CONTEXT_QUARTERLY}': injections.MARKET_CONTEXT_QUARTERLY || '',
      '{MARKET_CONTEXT_CURRENT}': injections.MARKET_CONTEXT_CURRENT || '',
      '{MARKET_DATA}': injections.MARKET_DATA_STRUCTURED || '{}'
    };

    // Replace all placeholders
    for (const [placeholder, value] of Object.entries(placeholders)) {
      enhancedContent = enhancedContent.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'gi'),
        value
      );
    }

    // If no explicit placeholders were found, append context as hidden prompt
    if (content === enhancedContent && injections.MARKET_CONTEXT_COMBINED) {
      // Inject as a hidden context section that won't appear in final output
      enhancedContent = `<!-- MARKET CONTEXT START -->
${injections.MARKET_CONTEXT_COMBINED}
<!-- MARKET CONTEXT END -->

${content}`;
    }

    return enhancedContent;
  }

  /**
   * Generate newsletter with retry logic
   * @param {Object} params - Generation parameters
   * @param {number} maxRetries - Maximum number of retries
   */
  async generateWithRetry(params, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.logger.info(`Generating newsletter (attempt ${attempt}/${maxRetries})...`);
        return await this.generateNewsletter(params);
      } catch (error) {
        lastError = error;
        this.logger.warn(`Attempt ${attempt} failed:`, error.message);

        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          this.logger.info(`Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    return lastError; // Return the last error (already formatted by generateNewsletter)
  }

  /**
   * Regenerate content with a specific writing style using AI
   * @param {string} content - Original content to transform
   * @param {Object} styleProfile - Style profile object with writingStyle, tone, complexity
   * @param {Object} options - Optional settings { preserveFormatting, maxLength }
   * @returns {Promise<Object>} { content, tokensUsed, estimatedCost }
   */
  async regenerateWithStyle(content, styleProfile, options = {}) {
    return _regenerateWithStyleImpl(this, content, styleProfile, options);
  }

  /**
   * Build style-specific instructions for AI
   * @private
   * @param {Object} styleProfile - Style profile object
   * @returns {string} Style instructions
   */
  _buildStyleInstructions(styleProfile) {
    return _buildStyleInstructionsImpl(styleProfile);
  }

  /**
   * Regenerate a single named section of the newsletter (Sprint 4 #66).
   *
   * Cost-optimized: pulls fresh AI-generated placeholder text from S3
   * (Rule 17 — fetchLatestPlaceholders, NOT static samples), assembles a
   * pre-filled section template, and asks the LLM to ONLY format/polish it
   * for the requested tone. The LLM does NOT generate the underlying
   * content — that already came from the daily Lambda batch.
   *
   * Compliance (compliance-newsletter skill): the system prompt must NOT
   * contain promissory phrases like "guaranteed returns" / "risk-free".
   *
   * @param {Object} params
   * @param {string} params.sectionKey - e.g. 'market_overview', 'economic_indicators',
   *   'investment_insights'. Maps to the placeholder namespace.
   * @param {Object} [params.marketData] - market data context (passed to S3 fetcher).
   * @param {Object} [params.userProfile] - advisor profile (for personalization).
   * @param {string} [params.tone='professional'] - writing tone for the polish step.
   * @param {string} [params.currentContent] - the section's existing markdown,
   *   used as a structural hint for the LLM (optional).
   * @param {boolean} [params.forceFresh=false] - bypass the in-memory cache.
   * @returns {Promise<{ content: string, metadata: Object, error?: string }>}
   */
  async regenerateSection(params = {}) {
    return _regenerateSectionImpl(this, params);
  }

  /**
   * Generate a single placeholder value using AI
   * @param {string} prompt - Prompt describing the placeholder
   * @returns {Promise<string>} Generated placeholder value
   */
  async generatePlaceholder(prompt) {
    return _generatePlaceholderImpl(this, prompt);
  }

  /**
   * Analyze writing style from sample text
   * @param {string} prompt - Analysis prompt with sample text
   * @returns {Promise<string>} JSON string with style analysis
   */
  async analyzeStyle(prompt) {
    return _analyzeStyleImpl(this, prompt);
  }

  /**
   * Generate AI-powered email subject line candidates from newsletter body content.
   *
   * Cost-optimized: sends pre-filled body content (no placeholder regeneration).
   * Compliance: filters SEC/FINRA banned phrases (guaranteed, risk-free, insider tip, etc.)
   * before returning candidates to the UI.
   *
   * @param {Object} params - Generation parameters
   * @param {string} params.content - Newsletter body content (pre-filled, plain text or HTML)
   * @param {string} [params.tone='professional'] - Tone for the subjects (professional, friendly, urgent, authoritative)
   * @param {number} [params.count=3] - Number of subject line candidates to return
   * @returns {Promise<string[]>} Array of subject line candidates (each <= 60 characters)
   */
  async generateSubjectLines(params = {}) {
    return _generateSubjectLinesImpl(this, params);
  }

  /**
   * @private — preserved as a delegate so any internal callers / tests
   * that reference the class method keep working.
   */
  _parseSubjectLineResponse(rawContent) {
    return _parseSubjectLineResponseImpl(rawContent);
  }

  /**
   * @private — preserved as a delegate so any internal callers / tests
   * that reference the class method keep working.
   */
  _isSubjectLineCompliant(subject) {
    return _isSubjectLineCompliantImpl(subject);
  }

  /**
   * Rewrite a selected piece of text using one of five actions.
   *
   * Sprint 5 #64 — powers the inline AI toolbar that surfaces when an
   * advisor highlights text in the Quill editor. Each action maps to a
   * tightly-scoped system prompt; the user-selected text is the user
   * prompt. SEC/FINRA guardrails are baked into every prompt; numbers,
   * dates, and percentages are explicitly preserved.
   *
   * @param {string} text - The selected text to rewrite (required, non-empty)
   * @param {'rewrite'|'shorter'|'more_formal'|'more_casual'|'simplify'} action
   * @param {Object} [options]
   * @param {Object} [options.style] - CommunicationStyle to adapt to
   * @returns {Promise<string>} The rewritten text (quotes stripped)
   */
  async rewriteSelection(text, action, options = {}) {
    return _rewriteSelectionImpl(this, text, action, options);
  }

  /**
   * Test the API health endpoint
   */
  async testConnection() {
    await this.initialize();

    if (!this.apiEndpoint) {
      return {
        success: false,
        error: 'Service not initialized'
      };
    }

    try {
      // Health endpoint doesn't require authentication
      const healthUrl = this.apiEndpoint.replace('/generate', '/health');
      const response = await fetch(healthUrl);

      return {
        success: response.ok,
        status: response.status,
        statusText: response.statusText
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate newsletter for multiple clients (batch processing)
   * @param {Array} clients - Array of client data
   * @param {Object} template - Template to use
   * @param {Object} options - Additional options
   */
  async batchGenerate(clients, template, options = {}) {
    const results = [];
    const {
      style,
      sections,
      parallel = false,
      includeMarketContext = true,
      newsletterType = 'weekly',
      onProgress
    } = options;

    // Pre-fetch market context once for all clients (if enabled)
    let sharedMarketContext = null;
    if (includeMarketContext) {
      try {
        this.logger.info('Pre-fetching market context for batch generation...');
        const marketContext = await this.marketContextService.getMarketContext({
          newsletterType,
          includeWeekly: true,
          includeQuarterly: newsletterType === 'quarterly',
          includeRealtime: true
        });
        sharedMarketContext = marketContext.prompt_injections;
        this.logger.info(`Market context cached for batch: ${Object.keys(sharedMarketContext).length} injections ready`);
      } catch (error) {
        this.logger.warn('Failed to pre-fetch market context for batch:', error);
      }
    }

    if (parallel) {
      // Process in parallel (be careful with rate limits)
      const promises = clients.map((client, index) =>
        this.generateWithRetry({
          templateContent: template.content,
          clientData: client,
          style,
          sections,
          includeMarketContext,
          newsletterType,
          // Pass pre-fetched context to avoid redundant API calls
          _cachedMarketContext: sharedMarketContext
        }).then(result => {
          if (onProgress) onProgress({ current: index + 1, total: clients.length });
          return {
            clientId: client.id,
            clientName: client.name,
            success: true,
            ...result
          };
        }).catch(error => {
          if (onProgress) onProgress({ current: index + 1, total: clients.length });
          return {
            clientId: client.id,
            clientName: client.name,
            success: false,
            error: error.message
          };
        })
      );

      const results = await Promise.all(promises);

      // Return summary with results
      return {
        total: results.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
        results
      };
    } else {
      // Process sequentially
      for (let i = 0; i < clients.length; i++) {
        const client = clients[i];
        try {
          const result = await this.generateWithRetry({
            templateContent: template.content,
            clientData: client,
            style,
            sections,
            includeMarketContext,
            newsletterType,
            // Pass pre-fetched context to avoid redundant API calls
            _cachedMarketContext: sharedMarketContext
          });

          results.push({
            clientId: client.id,
            clientName: client.name,
            success: true,
            ...result
          });
        } catch (error) {
          results.push({
            clientId: client.id,
            clientName: client.name,
            success: false,
            error: error.message
          });
        }

        // Report progress after each client
        if (onProgress) {
          onProgress({ current: i + 1, total: clients.length });
        }
      }

      // Return summary with results
      return {
        total: results.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
        results
      };
    }
  }

  // Placeholder generation methods merged from PlaceholderGenerationService (Phase 1 consolidation).
  // Used only when forceExpensivePlaceholderGeneration is enabled (disabled by default for cost reasons).
  // Wave 4 (#229): bodies extracted to newsletter-ai/placeholder-generation.js;
  // these stay as 1-line delegates so internal callers + tests keep working.

  async _generateAllPlaceholderValues(placeholders, context) {
    return _generateAllPlaceholderValuesImpl(this, placeholders, context);
  }

  async _generatePlaceholderValue(placeholder, context) {
    return _generatePlaceholderValueImpl(this, placeholder, context);
  }

  _buildPlaceholderPrompt(placeholder, clientData, advisorPrefs, marketContext) {
    return _buildPlaceholderPromptImpl(placeholder, clientData, advisorPrefs, marketContext);
  }

  _validateAndFormatPlaceholder(value, placeholder) {
    return _validateAndFormatPlaceholderImpl(value, placeholder);
  }

  _getPlaceholderFallbackValue(placeholder, clientData) {
    return _getPlaceholderFallbackValueImpl(placeholder, clientData);
  }
}

// Export singleton instance
// Create singleton instance
const newsletterAIServiceInstance = new NewsletterAIService();

// Check feature flag for singleton behavior
const USE_SINGLETON = import.meta.env?.VITE_USE_NEWSLETTER_AI_SINGLETON !== 'false' &&
                      process.env.REACT_APP_USE_NEWSLETTER_AI_SINGLETON !== 'false';

// Export based on feature flag
export default USE_SINGLETON ? newsletterAIServiceInstance : NewsletterAIService;

// Always export the class for testing
export { NewsletterAIService };

// ============================================================================
// #229 decomposition — re-exports of helpers extracted into newsletter-ai/
// ============================================================================
// External consumers (EmailService, tests, future callers) import these names
// from NewsletterAIService.js. The implementations live in focused modules
// under ./newsletter-ai/ so this facade stays focused on the class.

export {
  buildPromptContext,
  NICHE_FRAMING,
} from './newsletter-ai/series-prompt-context.js';

export {
  applyBrandingToHtml,
  safeColor,
} from './newsletter-ai/branding.js';

export {
  runCompliancePrechecks,
  applyComplianceDisclaimer,
  NICHE_DEFAULT_DISCLAIMERS,
} from './newsletter-ai/compliance-prechecks.js';
